// src/pages/CreatorProjectDetail.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, isApiError } from "../lib/api";

type ResolutionKey = "720p" | "1080p" | "2k" | "4k" | "8k";

type Generation = {
  id: string;

  // signed url (preferido)
  imageUrl?: string | null;

  // compat legacy/backends
  image_url?: string | null;
  output_url?: string | null;

  prompt?: string | null;
  style?: string | null;
  aspect_ratio?: string | null;
  resolution?: string | null;
  engine?: string | null;
  cost_credits?: number | null;

  created_at?: string | null;
};

type Project = {
  id: string;
  name: string;
  created_at?: string;
  source_mode?: "creator" | "brand" | null;
};

function pickImageUrl(g: Generation) {
  return (g.imageUrl || g.image_url || g.output_url || "")?.toString();
}

function normalizeResolutionKey(v?: string | null): ResolutionKey | null {
  const s = String(v || "").toLowerCase().trim();
  if (s === "720p") return "720p";
  if (s === "1080p") return "1080p";
  if (s === "2k") return "2k";
  if (s === "4k") return "4k";
  if (s === "8k") return "8k";
  return null;
}

function humanDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function friendlyApiError(err?: unknown) {
  const raw = String(err || "").trim();
  if (!raw) return "Ocurrió un error. Intenta de nuevo.";
  if (raw.includes("maintenance_mode")) return "El servidor está en mantenimiento. Intenta nuevamente en unos minutos.";
  if (raw.includes("rate_limited")) return "Estamos recibiendo muchas solicitudes. Intenta de nuevo en unos segundos.";
  return raw;
}

function NoisePlaceholder() {
  return (
    <div className="relative w-full h-full overflow-hidden">
      <div
        className="
          absolute inset-0
          bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.10),transparent_35%),radial-gradient(circle_at_80%_30%,rgba(255,255,255,0.08),transparent_40%),radial-gradient(circle_at_50%_80%,rgba(255,255,255,0.06),transparent_45%),linear-gradient(135deg,rgba(99,102,241,0.18),rgba(168,85,247,0.12),rgba(15,23,42,0.35))]
          blur-[2px]
        "
      />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.10),transparent)] animate-pulse opacity-40" />
      <div className="absolute inset-0 bg-black/30" />
    </div>
  );
}

export default function CreatorProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  const [projectName, setProjectName] = useState<string>("Proyecto");
  const [items, setItems] = useState<Generation[]>([]);

  // ✅ Preview central + blur->sharp
  const [mainId, setMainId] = useState<string | null>(null);
  const mainItem = useMemo(() => (mainId ? items.find((x) => x.id === mainId) || null : null), [items, mainId]);

  const [mainImgLoaded, setMainImgLoaded] = useState(false);
  const lastMainUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const url = mainItem ? pickImageUrl(mainItem) : null;
    if (url !== lastMainUrlRef.current) {
      lastMainUrlRef.current = url;
      setMainImgLoaded(false);
    }
  }, [mainItem]);

  // ✅ Modal preview (igual al estilo de Brand)
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState<Generation | null>(null);

  const openPreview = useCallback((g: Generation) => {
    setPreviewItem(g);
    setPreviewOpen(true);
  }, []);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewItem(null);
  }, []);

  const count = items.length;
  const subtitle = useMemo(() => `${count} creación${count === 1 ? "" : "es"}`, [count]);

  const loadAll = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    setError("");

    try {
      const token = await getAccessToken();
      if (!token) throw new Error("No hay sesión activa.");

      // 1) Nombre del proyecto (solo creator)
      try {
        const pRes = await api.projectsList(
          { sourceMode: "creator", limit: 200 },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const list: Project[] = Array.isArray((pRes as any)?.projects) ? (pRes as any).projects : [];
        const found = list.find((p) => String(p.id) === String(projectId));
        if (found?.name) setProjectName(found.name);
      } catch {
        // no bloqueamos
      }

      // 2) Generaciones SOLO creator del proyecto
      const gRes = await api.generationsList(
        { sourceMode: "creator", projectId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!gRes?.success) {
        throw new Error((gRes as any)?.error || "No se pudieron cargar las creaciones.");
      }

      const rawItems: Generation[] = Array.isArray((gRes as any)?.items) ? (gRes as any).items : [];

      // Orden más nuevo primero
      const sorted = [...rawItems].sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      });

      setItems(sorted);

      // Selección por defecto
      if (sorted.length > 0) {
        // si ya había mainId, lo respetamos si existe
        setMainId((prev) => {
          if (prev && sorted.some((x) => x.id === prev)) return prev;
          return sorted[0].id;
        });
      } else {
        setMainId(null);
      }
    } catch (e) {
      const msg = isApiError(e) ? e.message : e instanceof Error ? e.message : "No se pudieron cargar las creaciones.";
      setError(friendlyApiError(msg));
      setItems([]);
      setMainId(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, getAccessToken]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  return (
    <section className="max-w-6xl mx-auto space-y-4 text-white">
      {/* Header (igual que Brand Gallery) */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-gray-400">PROYECTO</div>
          <h1 className="text-2xl md:text-3xl font-bold mt-2">{projectName}</h1>
          <p className="text-sm text-gray-400 mt-1">{subtitle}</p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/dashboard?tab=proyectos")}
            className="h-10 px-4 rounded-xl text-sm font-semibold border border-white/15 hover:bg-white/10 transition"
          >
            ← Volver
          </button>

          <button
            type="button"
            onClick={loadAll}
            className="h-10 px-4 rounded-xl text-sm font-semibold border border-white/15 hover:bg-white/10 transition"
            disabled={loading}
            title="Refrescar"
          >
            {loading ? "Cargando..." : "🔄 Refrescar"}
          </button>

          <button
            type="button"
            onClick={() => navigate(`/dashboard?tab=ia&projectId=${encodeURIComponent(projectId || "")}`)}
            className="
              h-10 px-4 rounded-xl text-sm font-semibold
              bg-gradient-to-r from-indigo-500 to-purple-500
              hover:from-indigo-400 hover:to-purple-400 transition shadow-lg shadow-indigo-500/30
            "
          >
            ✨ Generar en este proyecto
          </button>
        </div>
      </div>

      {/* Error */}
      {!!error && !loading && (
        <div className="rounded-2xl border border-red-400/20 bg-red-500/10 p-4 text-sm text-red-200">
          {error}
          <div className="mt-2 text-[11px] text-red-200/70">
            Tip: si ves 401, es sesión/token. Aquí ya manda Authorization, así que casi siempre es sesión expirada.
          </div>
        </div>
      )}

      {/* Card principal (igual que Brand Gallery) */}
      <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-md shadow-2xl shadow-indigo-900/35">
        {/* PREVIEW */}
        <div className="relative rounded-t-3xl overflow-hidden">
          <div className="w-full bg-black/40 h-[40vh] md:h-[52vh] max-h-[560px] min-h-[300px]">
            {mainItem && pickImageUrl(mainItem) ? (
              <img
                src={pickImageUrl(mainItem)}
                alt=""
                draggable={false}
                onLoad={() => setMainImgLoaded(true)}
                className={[
                  "w-full h-full object-cover",
                  "transition-all duration-500 ease-out",
                  mainImgLoaded ? "opacity-100 blur-0 scale-100" : "opacity-0 blur-md scale-[1.01]",
                ].join(" ")}
              />
            ) : (
              <div className="w-full h-full relative">
                <NoisePlaceholder />
                {!loading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center px-6">
                      <div className="mx-auto w-12 h-12 rounded-2xl border border-white/10 bg-black/40 backdrop-blur flex items-center justify-center text-xl">
                        🖼️
                      </div>
                      <div className="mt-4 text-base font-semibold text-gray-100">Aún no hay imágenes aquí</div>
                      <div className="mt-2 text-sm text-gray-300/80 max-w-md mx-auto">
                        Genera tu primera imagen dentro de este proyecto.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {mainItem && pickImageUrl(mainItem) && (
              <button
                type="button"
                onClick={() => openPreview(mainItem)}
                className="absolute top-4 right-4 text-[11px] px-3 py-2 rounded-xl border border-white/15 bg-black/35 hover:bg-white/10 transition backdrop-blur"
              >
                Abrir preview ↗
              </button>
            )}
          </div>
        </div>

        {/* Meta debajo del preview (igual a Brand) */}
        <div className="p-4 md:p-5">
          {mainItem ? (
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-400">
              <span className="px-2 py-1 rounded-full bg-black/30 border border-white/10">
                {(mainItem.style || "—").toString()}
              </span>

              <span className="px-2 py-1 rounded-full bg-black/30 border border-white/10">
                {(normalizeResolutionKey(mainItem.resolution) || "—").toString().toUpperCase()}
              </span>

              <span className="px-2 py-1 rounded-full bg-black/30 border border-white/10">
                {mainItem.cost_credits ? `${mainItem.cost_credits} créditos` : "—"}
              </span>

              <span className="px-2 py-1 rounded-full bg-black/30 border border-white/10">
                {(mainItem.aspect_ratio || "—").toString()}
              </span>

              <span className="px-2 py-1 rounded-full bg-black/30 border border-white/10">
                {humanDate(mainItem.created_at)}
              </span>

              {mainItem.prompt && (
                <span className="px-2 py-1 rounded-full bg-black/30 border border-white/10 text-gray-200">
                  {String(mainItem.prompt).slice(0, 70)}
                  {String(mainItem.prompt).length > 70 ? "…" : ""}
                </span>
              )}
            </div>
          ) : (
            <div className="text-[11px] text-gray-500">—</div>
          )}
        </div>
      </div>

      {/* Grid de imágenes (igual que Brand Gallery) */}
      <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-md p-4 md:p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm font-semibold">Imágenes del proyecto</div>
          <div className="text-[11px] text-gray-500">{loading ? "Cargando…" : `${items.length} elemento(s)`}</div>
        </div>

        {!loading && !error && items.length === 0 && (
          <div className="mt-5 rounded-2xl border border-white/10 bg-black/20 p-8 text-center text-gray-400">
            <div className="text-base font-semibold text-gray-200">Este proyecto aún no tiene creaciones</div>
            <p className="text-sm mt-2">Empieza generando tu primera imagen en este proyecto.</p>

            <div className="mt-6">
              <button
                onClick={() => navigate(`/dashboard?tab=ia&projectId=${encodeURIComponent(projectId || "")}`)}
                className="px-6 py-3 bg-white text-black rounded-xl font-semibold hover:bg-gray-200 transition"
              >
                Generar primera imagen →
              </button>
            </div>
          </div>
        )}

        {items.length > 0 && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {items.map((g) => {
              const url = pickImageUrl(g);
              const rk = normalizeResolutionKey(g.resolution);

              return (
                <button
                  key={g.id}
                  onClick={() => setMainId(g.id)}
                  onDoubleClick={() => openPreview(g)}
                  className={`
                    text-left rounded-2xl border overflow-hidden transition
                    ${
                      mainId === g.id
                        ? "border-indigo-400/60 bg-indigo-500/10"
                        : "border-white/10 bg-black/25 hover:bg-white/5 hover:border-indigo-400/30"
                    }
                  `}
                  title={String(g.prompt || "Abrir")}
                >
                  <div className="relative">
                    {url ? (
                      <div className="w-full aspect-video overflow-hidden">
                        <img src={url} alt="" className="w-full h-full object-cover object-center" draggable={false} />
                      </div>
                    ) : (
                      <div className="aspect-video bg-gradient-to-br from-indigo-800/40 via-purple-800/40 to-slate-900/60" />
                    )}

                    <div className="absolute top-2 left-2 text-[10px] px-2 py-1 rounded-full bg-black/55 border border-white/10 text-gray-100">
                      {(rk || "—").toString().toUpperCase()}
                    </div>

                    <div className="absolute top-2 right-2 text-[10px] px-2 py-1 rounded-full bg-black/55 border border-white/10 text-gray-100">
                      {g.cost_credits ? `${g.cost_credits}c` : "—"}
                    </div>
                  </div>

                  <div className="p-2.5">
                    <div className="text-[11px] text-gray-200 truncate">{String(g.prompt || "—")}</div>
                    <div className="mt-1 text-[10px] text-gray-400 truncate">
                      {String(g.style || "—")} · {String(g.aspect_ratio || "—")}
                    </div>

                    <div className="mt-2 flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openPreview(g);
                        }}
                        className="text-[10px] px-2 py-1 rounded-lg border border-white/10 hover:bg-white/10 transition text-gray-200"
                      >
                        Abrir ↗
                      </button>

                      <span className="text-[10px] text-gray-500">{g.created_at ? new Date(g.created_at).toLocaleDateString() : "—"}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-3 text-[11px] text-gray-500">
          Tip: Click = seleccionar para preview central · Doble click o “Abrir” = preview completo.
        </div>
      </div>

      {/* ✅ MODAL: Preview */}
      {previewOpen && previewItem && (
        <div className="fixed inset-0 z-50 bg-black/80 px-4 py-6 overflow-y-auto" onClick={closePreview}>
          <div
            className="
              w-full max-w-5xl mx-auto
              rounded-2xl border border-white/10 bg-[#0c0c14] shadow-2xl overflow-hidden
              flex flex-col
              max-h-[90vh]
            "
            onClick={(e) => e.stopPropagation()}
          >
            {/* HEADER */}
            <div className="p-4 border-b border-white/10 flex items-start justify-between gap-4 shrink-0">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-white truncate">{String(previewItem.prompt || "Preview")}</div>

                <div className="mt-1 text-[11px] text-gray-400 flex flex-wrap gap-2 items-center">
                  <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">
                    {(normalizeResolutionKey(previewItem.resolution) || "—").toUpperCase()}
                  </span>
                  <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">
                    {previewItem.style || "—"}
                  </span>
                  <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">
                    {previewItem.aspect_ratio || "—"}
                  </span>
                  <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">
                    {previewItem.cost_credits ? `${previewItem.cost_credits} créditos` : "—"}
                  </span>
                  <span className="text-gray-500">· {humanDate(previewItem.created_at)}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {pickImageUrl(previewItem) && (
                  <a
                    href={pickImageUrl(previewItem)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[11px] px-3 py-2 rounded-lg border border-white/10 hover:bg-white/10 transition text-gray-200"
                    title="Abrir imagen en nueva pestaña"
                  >
                    ↗ Abrir
                  </a>
                )}
                <button
                  onClick={closePreview}
                  className="text-[11px] px-3 py-2 rounded-lg border border-white/10 hover:bg-white/10 transition text-gray-200"
                >
                  ✕ Cerrar
                </button>
              </div>
            </div>

            {/* BODY SCROLL */}
            <div className="p-4 overflow-y-auto">
              <div className="rounded-2xl border border-white/10 bg-black/40 overflow-auto">
                {pickImageUrl(previewItem) ? (
                  <img src={pickImageUrl(previewItem)} alt="" className="w-full h-auto block" draggable={false} />
                ) : (
                  <div className="h-80 bg-gradient-to-br from-indigo-800/40 via-purple-800/40 to-slate-900/60" />
                )}
              </div>

              <div className="mt-3 text-[11px] text-gray-500">
                Tip: abre la imagen en nueva pestaña y haz zoom al 200–300% para notar nitidez/detalle.
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

